/// <reference types="vite/client" />

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider, type Theme } from '@mui/material/styles';
import { ErrorBoundary } from 'react-error-boundary';
import React, { Suspense, lazy, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { chooseTheme } from './theme';
import { AppContext, AppProvider } from './context/AppContext';
import { Button, IconButton, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Loading from './components/Loading';
import { TranslationsProvider, useTranslation } from './utils/i18n';
import { RedirectExternal } from './components/RedirectExternal';
import NativeRuntime from './components/NativeRuntime';
import DriveSyncRuntime from './components/DriveSyncRuntime';
import { uploadDebugLogs } from './utils/debugLog';
import AndroidAppPromotionPrompt from './components/AndroidAppPromotionPrompt';

const Reader = lazy(async () => {
  const module = await import('./components/Reader');
  return { default: module.Reader };
});
const BookList = lazy(() => import('./components/BookList'));
const HandleShare = lazy(() => import('./components/HandleShare'));
const LoginPage = lazy(() => import('./components/LoginPage'));

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

function ErrorFallbackContent({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const t = useTranslation();
  const [feedbackDescription, setFeedbackDescription] = React.useState('');
  const [feedbackState, setFeedbackState] = React.useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const [feedbackError, setFeedbackError] = React.useState<string | null>(null);

  React.useEffect(() => {
    console.error('React error boundary caught a hard app error', {
      message: error.message,
      stack: error.stack,
    });
  }, [error]);

  const handleReload = () => {
    resetErrorBoundary();
    window.location.reload();
  };

  const handleSubmitFeedback = async () => {
    const description = [
      `React error boundary: ${error.message}`,
      feedbackDescription.trim(),
    ].filter(Boolean).join('\n\n');

    setFeedbackState('uploading');
    setFeedbackError(null);

    try {
      await uploadDebugLogs(description, 'react-error-boundary');
      setFeedbackState('done');
      setFeedbackDescription('');
    } catch (uploadError) {
      setFeedbackState('error');
      setFeedbackError(uploadError instanceof Error ? uploadError.message : String(uploadError));
    }
  };

  return (
    <div
      role='alert'
      style={{
        minHeight: '100vh',
        boxSizing: 'border-box',
        padding: 24,
        background: '#120000',
        color: '#fff',
        fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif',
      }}
    >
      <h1 style={{ margin: '0 0 12px', fontSize: 20, lineHeight: 1.25 }}>{t('ERROR_BOUNDARY_TITLE')}</h1>
      <pre
        style={{
          margin: '0 0 20px',
          padding: 12,
          border: '1px solid rgba(255,255,255,.25)',
          borderRadius: 6,
          background: 'rgba(255,255,255,.08)',
          color: '#fff',
          whiteSpace: 'pre-wrap',
          overflow: 'auto',
          maxHeight: '34vh',
        }}
      >
        {error.message}
      </pre>
      <div style={{ display: 'grid', gap: 10, maxWidth: 720 }}>
        <label htmlFor='react-error-feedback' style={{ fontWeight: 600 }}>
          {t('FEEDBACK_SEND')}
        </label>
        <textarea
          id='react-error-feedback'
          rows={5}
          value={feedbackDescription}
          onChange={(event) => setFeedbackDescription(event.target.value)}
          placeholder={t('FEEDBACK_CONTEXT_PLACEHOLDER')}
          style={{
            boxSizing: 'border-box',
            width: '100%',
            border: '1px solid rgba(255,255,255,.35)',
            borderRadius: 6,
            padding: 10,
            background: '#fff',
            color: '#111',
            font: '14px/1.4 -apple-system,BlinkMacSystemFont,Segoe UI,sans-serif',
          }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button
            variant='contained'
            onClick={() => {
              void handleSubmitFeedback();
            }}
            disabled={feedbackState === 'uploading' || feedbackState === 'done'}
          >
            {feedbackState === 'uploading' ? t('FEEDBACK_SENDING') : feedbackState === 'done' ? t('FEEDBACK_SENT') : t('FEEDBACK_SEND')}
          </Button>
          <Button variant='outlined' color='inherit' onClick={handleReload}>
            {t('RELOAD')}
          </Button>
        </div>
        {feedbackState === 'done' && <div>{t('FEEDBACK_SENT_MESSAGE')}</div>}
        {feedbackState === 'error' && feedbackError && <div>{feedbackError}</div>}
      </div>
    </div>
  );
}

function ErrorFallback(props: ErrorFallbackProps) {
  return <TranslationsProvider><ErrorFallbackContent {...props} /></TranslationsProvider>;
}

const ThemedApp = () => {
  const t = useTranslation();
  const { effectiveTheme, snackbarMessage, setSnackbarMessage, localForageLoaded, user } = useContext(AppContext);
  const [theme, setTheme] = useState<Theme | undefined>();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const snackbarSequenceRef = useRef(0);

  useEffect(() => {
    // Only start timeout if we're still waiting for localForage or theme
    if (localForageLoaded && theme) {
      return;
    }

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setLoadingTimeout(true);
      console.error('Loading timeout reached - forcing app render');
      // Force default theme if needed
      if (!theme) {
        setTheme(chooseTheme('light'));
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeoutId);
  }, [localForageLoaded, theme]); // Add dependencies to re-run effect when these change

  useEffect(() => {
    if (effectiveTheme) {
      setTheme(chooseTheme(effectiveTheme));
    }
  }, [effectiveTheme]);

  useEffect(() => {
    if (snackbarMessage !== null) {
      console.log('snackbar message set to', snackbarMessage);
    }
  }, [snackbarMessage]);

  const handleSnackbarClose = (reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    const onDismiss = snackbarMessage?.onDismiss;
    setSnackbarMessage(null);

    if (onDismiss) {
      void Promise.resolve(onDismiss()).catch((error) => {
        console.error('Snackbar dismiss handler failed', error);
      });
    }
  };

  const snackbarKey = useMemo(() => {
    if (!snackbarMessage) {
      return undefined;
    }

    snackbarSequenceRef.current += 1;
    return snackbarSequenceRef.current;
  }, [snackbarMessage]);

  return loadingTimeout || (localForageLoaded && theme) ? (
    <ThemeProvider theme={theme || chooseTheme('light')}>
      <div className='relative w-full bg-stone-100 dark:bg-black' style={{ margin: 0 }}>
        <div className='max-w-6xl mx-auto grid grid-cols-1' style={{ margin: 0 }}>
          <main>
            <Suspense fallback={<Loading text={t('LOADING_PAGE')} />}>
              <NativeRuntime />
              <DriveSyncRuntime />
              <AndroidAppPromotionPrompt />
              <Routes>
                <Route path='/about' element={<RedirectExternal url={`${import.meta.env.BASE_URL}about/`} />} />
                <Route path='/tos' element={<RedirectExternal url={`${import.meta.env.BASE_URL}tos/`} />} />
                <Route path='/privacy' element={<RedirectExternal url={`${import.meta.env.BASE_URL}privacy/`} />} />
                <Route
                  path='/account-deletion'
                  element={<RedirectExternal url={`${import.meta.env.BASE_URL}account-deletion/`} />}
                />
                <Route
                  path='/handle_epub'
                  element={<HandleShare setSnackbarMessage={setSnackbarMessage} user={user} />}
                />
                <Route path='/:cmd?' element={user ? <BookList /> : <Navigate to='/login' replace />} />
                <Route path='/read/:id/:uriLocation?' element={user ? <Reader /> : <Navigate to='/login' replace />} />
                <Route path='/login' element={user ? <Navigate to='/' replace /> : <LoginPage />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>
      {Boolean(snackbarMessage && snackbarMessage.text) && (
        <Snackbar
          key={snackbarKey}
          open={Boolean(snackbarMessage)}
          autoHideDuration={snackbarMessage?.duration === null ? null : (snackbarMessage?.duration ?? 10000)}
          onClose={(_event, reason) => {
            handleSnackbarClose(reason);
          }}
          message={snackbarMessage?.text}
          action={
            snackbarMessage?.actionLabel || snackbarMessage?.dismissible ? (
              <>
                {snackbarMessage?.actionLabel && snackbarMessage.onAction && (
                  <Button
                    color='secondary'
                    size='small'
                    onClick={() => {
                      void Promise.resolve(snackbarMessage.onAction?.())
                        .then(() => {
                          if (snackbarMessage.dismissOnAction) {
                            setSnackbarMessage(null);
                          }
                        })
                        .catch((error) => {
                          console.error('Snackbar action failed', error);
                        });
                    }}
                  >
                    {snackbarMessage.actionLabel}
                  </Button>
                )}
                {snackbarMessage?.dismissible && (
                  <IconButton
                    aria-label={snackbarMessage.dismissLabel || t('DISMISS_NOTIFICATION')}
                    color='inherit'
                    size='small'
                    onClick={() => handleSnackbarClose('dismiss-button')}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                )}
              </>
            ) : undefined
          }
        />
      )}
    </ThemeProvider>
  ) : (
    <Loading />
  );
};

const App = () => {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // Reset the state of your application so the error doesn't happen again
      }}
    >
      <AppProvider>
        <TranslationsProvider>
          <BrowserRouter basename={import.meta.env.BASE_URL}>
            <ThemedApp />
          </BrowserRouter>
        </TranslationsProvider>
      </AppProvider>
    </ErrorBoundary>
  );
};

export default App;
